import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from "react-redux";
import {compose} from "redux";
import DataGrid, {SelectColumn} from 'react-data-grid';
import {debounce} from "lodash";
import {Box, Button, Tooltip} from "@mui/material";
import 'react-data-grid/lib/styles.css';
import moment from "moment/moment";
import {useHistory, useLocation} from "react-router-dom";
import {IsAtBottom} from "../../reporting-table/ReactDataGridUtils";
import CustomSnackbar from "../../alert/CustomSnackbar";
import Spinner from "../../spinner/Spinner";
import {workspaceActions} from "../../../store/actions/workspaceAction";
import {alertActions} from "../../../store/actions/alertActions";
import {websiteBucketsActions} from "../../../store/actions/websiteBucketsAction";
import {Add, CloudUpload, Delete, PlayArrow} from "@mui/icons-material";

import {WebsiteSectionHeader, WebsiteTableCard, WebsiteEmptyState, WebsiteDeleteDialog} from "../WebsitesDesign";
import {StorageOutlined} from "@mui/icons-material";

function BucketsTable(props) {

    const history = useHistory();
    const location = useLocation();

    const {
        alert, getReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows,
        getWorkspaceIDs, resetUpdatedWorkspaceValue, alertError, deleteBucket
    } = props;
    const {data, isLoading, bucket, orderBy, sortBy, sortColumns, rerender} = props?.websiteBucketsState;
    const {plan} = props?.auth?.subscription;

    const [prompt, setPrompt] = useState(false);
    const [deleteBucketId, setDeleteBucketId] = useState(0);
    const [selectedRows, setSelectedRows] = useState(() => new Set());

    const getColumns = () => {
        let cachedColumns = JSON.parse(localStorage.getItem("website-buckets-columns"));
        let columns = [
            SelectColumn,
            {key: 'id', name: 'ID', hidden: false, width: 70, frozen: false, sortable: true, resizable: true},
            {
                key: 'workspace',
                name: 'Workspace',
                hidden: false,
                width: 105,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'integration',
                name: 'Integration',
                hidden: false,
                width: 115,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'name',
                name: 'Website Name',
                hidden: false,
                width: 170,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'bucketName',
                name: 'S3 Bucket Name',
                hidden: false,
                width: 165,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'region',
                name: 'S3 Bucket Region',
                hidden: false,
                width: 143,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'websiteUrl',
                name: 'S3 Origin URL',
                hidden: false,
                width: 490,
                sortable: false,
                draggable: true,
                resizable: true
            },
            {
                key: 'createdAt',
                name: 'Created At',
                hidden: false,
                width: 190,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return row?.createdAt === null ? "-" : moment.utc(row?.createdAt).format("YYYY-MM-DD HH:mm") + " (UTC)"
                }
            },
        ];

        if (cachedColumns !== null && cachedColumns?.length !== columns.length) {
            return columns;
        }

        if (cachedColumns !== null && cachedColumns !== "" && cachedColumns !== undefined) {
            let sortedColumns = [];
            cachedColumns?.forEach(cachedCol => {
                columns?.forEach(defCol => {
                    if (cachedCol?.key === defCol?.key) {
                        defCol.width = cachedCol.width;
                        sortedColumns.push(defCol);
                    }
                });
            });
            return sortedColumns;
        }
        localStorage.setItem("website-buckets-columns", JSON.stringify(columns));
        return columns;
    };

    const gridRef = useRef(null);
    const pendingPage = useRef(null);
    const checkedData = useRef(null);

    const [columns, setColumns] = useState(getColumns());

    const onColumnsReorder = (sourceKey, targetKey) => {
        const sourceColumnIndex = columns.findIndex((c) => c.key === sourceKey);
        const targetColumnIndex = columns.findIndex((c) => c.key === targetKey);
        const reorderedColumns = [...columns];
        reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
        localStorage.setItem("website-buckets-columns", JSON.stringify(reorderedColumns));
        setColumns(reorderedColumns);
    };

    const handleInfinityScroll = useCallback((event) => {
        if (isLoading || pendingPage.current !== null || !IsAtBottom(event)) {
            return;
        }
        const page = Number(data?.page);
        const pageSize = Number(data?.recordsPerPage);
        const total = Number(data?.totalRecords);
        if (page >= 1 && pageSize > 0 && data?.records?.length && data?.recordsReturned !== 0 && page < Math.ceil(total / pageSize)) {
            pendingPage.current = page + 1;
            loadMoreRows(bucket, page + 1, orderBy, sortBy, getWorkspaceIDs()).finally(() => {
                pendingPage.current = null;
            });
        }
    }, [isLoading, data, bucket, orderBy, sortBy, loadMoreRows, getWorkspaceIDs]);

    // Small pages may not overflow the grid yet, so there is no scroll event to load the next one.
    useEffect(() => {
        if (isLoading || checkedData.current === data) return;
        const frame = requestAnimationFrame(() => {
            const element = gridRef.current?.element;
            if (!element || element.clientHeight === 0) return;
            checkedData.current = data;
            handleInfinityScroll({currentTarget: element});
        });
        return () => cancelAnimationFrame(frame);
    }, [data, isLoading, handleInfinityScroll]);

    const handleGetReport = () => {
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(bucket, 1, orderBy, sortBy, getWorkspaceIDs());
    };

    const cacheColumnResize = (idx, width) => {
        let cachedColumns = JSON.parse(localStorage.getItem("website-buckets-columns"));
        let modColumns = [];
        cachedColumns?.forEach((item, index) => {
            if (index === idx) {
                item.width = width;
            }
            modColumns.push(item);
        });
        localStorage.setItem("website-buckets-columns", JSON.stringify(modColumns));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(cacheColumnResize, 50), []);

    const handleClickOnAddBucket = () => {

        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        history.push({
            pathname: "/websites/s3-buckets/create",
            state: {background: location}
        });
    };

    const handleClickOnUploadFiles = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                history.push({
                    pathname: "/websites/s3-buckets/upload/" + parseInt(selectedRowId),
                    state: {background: location}
                });
            }
        }
    };

    const handleClickOnDeploy = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                history.push({
                    pathname: "/websites/s3-buckets/deploy/" + parseInt(selectedRowId),
                    state: {background: location}
                });
            }
        }
    };

    const handleDeletePrompt = () => {

        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                setDeleteBucketId(selectedRowId);
                setPrompt(true);
            }
        }
    };

    const handleOnClickDeleteBucket = (prompt) => {
        if (!prompt) {
            setPrompt(false);
            return false;
        } else {
            deleteBucket(deleteBucketId);
            setPrompt(false);
        }
    };

    useEffect(() => {
        handleGetReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getReport, bucket, sortBy, orderBy]);

    // Only for rerender
    useEffect(() => {
        if (rerender) {
            handleGetReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            }
        }

        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);

    }, [updateOrderBy, updateSortBy, sortColumns]);

    // 6. Rerender if user workspaces has changed.
    useEffect(() => {
        if (props?.workspace?.updated !== null) {
            handleGetReport();
            resetUpdatedWorkspaceValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.workspace?.updated])

    return <Box>
        <WebsiteSectionHeader title="S3 Buckets"
                              description="Upload your website as a ZIP file, manage its S3 bucket, and deploy it to a verified domain.">
            <Button variant="contained" startIcon={<Add/>} onClick={handleClickOnAddBucket}
                    sx={{minHeight: 40, borderRadius: '8px', px: 2}}>Create bucket</Button>
        </WebsiteSectionHeader>
        {prompt && <WebsiteDeleteDialog question="Delete S3 bucket?"
                                        text="All files in this bucket will be permanently deleted. This cannot be undone."
                                        confirmLabel="Delete bucket" confirm={handleOnClickDeleteBucket}/>}
        {alert.errorMsg && <CustomSnackbar message={alert.errorMsg} timeout={8000}/>}
        {alert.successMsg && <CustomSnackbar message={alert.successMsg} severity="success" timeout={8000}/>}
        <WebsiteTableCard title="S3 Buckets" count={isLoading ? null : data?.totalRecords ?? data?.records?.length ?? 0}
                          rowCount={data?.records?.length || 0} selectedCount={selectedRows.size} actions={<>
            <Button variant="outlined" color="inherit" startIcon={<PlayArrow/>} disabled={selectedRows.size !== 1}
                    onClick={handleClickOnDeploy}>Deploy</Button>
            <Button variant="outlined" color="inherit" startIcon={<CloudUpload/>} disabled={selectedRows.size !== 1}
                    onClick={handleClickOnUploadFiles}>Upload files</Button>
            <Tooltip title="Delete bucket"><span><Button variant="outlined" color="error" aria-label="Delete bucket"
                                                         disabled={selectedRows.size !== 1} onClick={handleDeletePrompt}
                                                         sx={{minWidth: 35, px: 1}}><Delete fontSize="small"/></Button></span></Tooltip>
        </>}>
            {isLoading && <Spinner size={32} overComponentBox/>}
            {columns !== null && (
                <DataGrid
                    ref={gridRef}
                    rowKeyGetter={(row) => row?.id}
                    columns={columns}
                    onColumnsReorder={onColumnsReorder}
                    rows={data?.records ? data.records : []}
                    defaultColumnOptions={{
                        sortable: true,
                        resizable: true,
                        draggable: true
                    }}
                    onColumnResize={(idx, width) => handleColumnResize(idx, width)}
                    sortColumns={sortColumns}
                    onSortColumnsChange={updateSortColumns}
                    selectedRows={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                    onSelectedCellChange={({rowIdx, row, column}) => {
                        if (rowIdx !== -1 && column?.key !== "select-row") {
                            setSelectedRows(new Set().add(row?.id));
                        }
                    }}
                    onScroll={handleInfinityScroll}
                    enableVirtualization={false}
                    className="website-data-grid"
                    rowHeight={55}
                    headerRowHeight={48}
                    aria-label="S3 Buckets"
                />
            )}
            {!isLoading && !data?.records?.length && <WebsiteEmptyState icon={StorageOutlined} title="No S3 buckets yet"
                                                                        description="Create a bucket and upload your website files to get started."/>}
        </WebsiteTableCard>
    </Box>;
}

const mapStateToProps = (state) => {
    return {
        websiteBucketsState: state.websiteBuckets,
        auth: state.auth,
        alert: state.alert,
        workspace: state.workspace
    }
};

const actionCreators = {
    getReport: websiteBucketsActions.read,
    loadMoreRows: websiteBucketsActions.readMoreRows,
    updateOrderBy: websiteBucketsActions.updateOrderBy,
    updateSortBy: websiteBucketsActions.updateSortBy,
    updateBucket: websiteBucketsActions.updateBucket,
    updateSortColumns: websiteBucketsActions.updateSortColumns,
    deleteBucket: websiteBucketsActions.deleteBucket,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue,
    alertError: alertActions.error,
};

export default compose(connect(mapStateToProps, actionCreators))(BucketsTable);
